import { catchError, throwError } from "rxjs";
import { Injectable } from '@angular/core';
import { Action, Selector, State, Store, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import { TextExportAuthorActions } from '@medsurf/flat-actions';
import * as FlatServices from '@medsurf/flat-services';
import * as FlatStates from '../../internal';

export interface TextExportAuthorStateModel {
  isLoading: { [id: string]: boolean };
  hasLoaded: { [id: string]: boolean },
  textExportObjects: { [id: string]: FlatModels.TextExportAuthorModels.TextExportObject[] };
}

@State<TextExportAuthorStateModel>({
  name: 'textExportAuthor',
  defaults: {
    isLoading: {},
    hasLoaded: {},
    textExportObjects: {},
  }
})
@Injectable()
export class TextExportAuthorState {
  constructor(protected store: Store,
              protected apiControlService: FlatServices.ApiControlService) {
  }

  //<editor-fold desc="Selectors">

  @Selector([TextExportAuthorState])
  static isLoading$(state: TextExportAuthorStateModel) {
    return state.isLoading;
  }

  @Selector([
    TextExportAuthorState.isLoading$,
    FlatStates.NodeEntityState.currentSelectedNode$
  ])
  static currentIsLoading$(isLoaded: { [id: string]: boolean },
                           currentSelectedNode: FlatModels.NodeEntityModels.NodeEntityType) {
    if (currentSelectedNode && isLoaded) {
      return isLoaded[currentSelectedNode.id] || false;
    }
    return false;
  }

  @Selector([TextExportAuthorState])
  static hasLoaded$(state: TextExportAuthorStateModel) {
    return state.hasLoaded;
  }

  @Selector([
    TextExportAuthorState.hasLoaded$,
    FlatStates.NodeEntityState.currentSelectedNode$
  ])
  static currentHasLoaded$(hasLoaded: { [id: string]: boolean },
                           currentSelectedNode: FlatModels.NodeEntityModels.NodeEntityType) {
    if (currentSelectedNode && hasLoaded) {
      return hasLoaded[currentSelectedNode.id] || false;
    }
    return false;
  }

  @Selector([TextExportAuthorState])
  static textExportObjects$(state: TextExportAuthorStateModel) {
    return state.textExportObjects;
  }

  @Selector([
    TextExportAuthorState.textExportObjects$,
    FlatStates.NodeEntityState.currentSelectedNode$
  ])
  static currentTextExportObjects$(textExportObjects: { [id: string]: FlatModels.TextExportAuthorModels.TextExportObject[] },
                                   currentSelectedNode: FlatModels.NodeEntityModels.NodeEntityType) {
    if (currentSelectedNode && textExportObjects) {
      return textExportObjects[currentSelectedNode.id] !== undefined ? textExportObjects[currentSelectedNode.id] : [];
    }
    return [];
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  @Action(TextExportAuthorActions.GetTextExports)
  getTextExports({getState, patchState}: StateContext<TextExportAuthorStateModel>,
                 {id}: TextExportAuthorActions.GetTextExports) {
    // Get Is Loading
    const isLoading = structuredClone(getState().isLoading);

    // Change Is Loading
    isLoading[id] = true;

    // Patch State
    patchState({
      isLoading
    });

    // Get Text Exports
    return this.apiControlService.sendMessage(
      new TextExportAuthorActions.GetTextExportRequest(id)
    ).pipe(
      catchError(result => {
        // Change Is Loading
        const isLoading = structuredClone(getState().isLoading);
        isLoading[id] = false;

        // Patch State
        patchState({
          isLoading
        });
        
        // Return error
        return throwError(() => {
          return result;
        });
      })
    );
  }

  @Action(TextExportAuthorActions.GetTextExportsSuccess)
  getTextExportsSuccess({getState, patchState}: StateContext<TextExportAuthorStateModel>,
                        {entityQueryResults}: TextExportAuthorActions.GetTextExportsSuccess) {
    for (const entityQueryResult of entityQueryResults) {
      // Get id
      const id = entityQueryResult.entity.id;

      // Get Text Export Objects
      const textExportObjects = structuredClone(getState().textExportObjects);

      textExportObjects[id] = entityQueryResult.entity.results;

      // Get Has Loaded
      const hasLoaded = structuredClone(getState().hasLoaded);

      // Change Has Loaded
      hasLoaded[id] = true;

      // Get Is Loading
      const isLoading = structuredClone(getState().isLoading);

      // Change Is Loading
      isLoading[id] = false;

      // Patch State
      patchState({
        textExportObjects,
        hasLoaded,
        isLoading
      });
    }
  }

  //</editor-fold>
}
